import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Add, Close } from '@material-ui/icons';
import Box from 'Components/Atoms/Box';
import LockedProductIcon from 'Components/Atoms/Icons/LockedProduct';
import {
  AppSettings,
  SecurityPlus,
} from 'types/shared/documents/restaurants';
import { classHelper } from 'utils/helper';
import { EditorProduct } from './products';
import {
  arrangeProducts,
  hideBanner,
  hideProduct,
  LayoutCandidate,
  moveShownProduct,
  ProductArrangement,
  ProductLayout,
  restoreBanner,
  restoreProduct,
  sameLayout,
  withSavedPositions,
} from './productLayout';
import {
  currentHolders,
  ProtectionError,
  RightsChanges,
} from './securityRights';
import { LayoutVersionError } from './saveDashboardEdits';
import AddProductsSheet, { AddableProduct } from './AddProductsSheet';
import ProtectionPanel from './ProtectionPanel';

/** A dashboard banner that edit mode lets the restaurant remove. */
export type DashboardBanner = {
  id: string;
  label: string;
  content: React.ReactNode;
};

type Props = {
  restaurantId: string;
  products: EditorProduct[];
  candidates: LayoutCandidate[];
  arrangement: ProductArrangement;
  /** Products the restaurant added on request (stored layout). */
  savedShown: string[];
  /** The stored order (without hidden products). */
  savedOrder: string[];
  savedHiddenBanners: string[];
  /** The banners the dashboard would show, removable while editing. */
  banners: DashboardBanner[];
  /** Where the dashboard wants the Fertig button; else it sits in the toolbar. */
  doneButtonContainer?: HTMLElement | null;
  /**
   * Products that keep their place in the draft while they cannot be shown
   * (availability not known yet), so the place survives every edit.
   */
  keepPlaceOf: string[];
  securityPlus: AppSettings<SecurityPlus> | null;
  securityPlusUsable: boolean;
  /** Resolves once the layout and the rights are stored together. */
  onSave: (edits: {
    layout: ProductLayout | null;
    rights: RightsChanges;
  }) => Promise<void>;
  onClose: () => void;
};

type Sheet = null | { kind: 'add' } | { kind: 'protection'; productId: string };

type Drag = {
  id: string;
  pointerId: number;
  x: number;
  y: number;
  /** Where the tile was grabbed, so the preview stays under the pointer. */
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  moved: boolean;
};

type DragPreview = {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

const DRAG_THRESHOLD_PX = 8;

const saveErrorMessage = (error: any) => {
  if (error instanceof ProtectionError || error instanceof LayoutVersionError) {
    return error.message;
  }
  if (error?.code === 'permission-denied') {
    return 'Du hast keine Berechtigung, die Produkte zu ändern.';
  }
  return 'Speichern fehlgeschlagen. Prüfe die Verbindung und versuche es erneut.';
};

const sameHolders = (a: string[], b: string[]) =>
  a.length === b.length && a.every((holder) => b.includes(holder));

const gridColumns = (grid: HTMLElement | null) => {
  if (!grid) return 1;
  const columns = window
    .getComputedStyle(grid)
    .gridTemplateColumns.split(' ')
    .filter(Boolean).length;
  return Math.max(1, columns);
};

/** The product grid in edit mode, in place of the normal icons. */
const ProductLayoutEditor = ({
  restaurantId,
  products,
  candidates,
  arrangement,
  savedShown,
  savedOrder,
  savedHiddenBanners,
  banners,
  doneButtonContainer,
  keepPlaceOf,
  securityPlus,
  securityPlusUsable,
  onSave,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();

  // The draft is the restaurant's order; the grid shows what can be shown of it.
  const [initial] = useState<ProductLayout>(() =>
    withSavedPositions(
      {
        order: arrangement.grid,
        hidden: arrangement.hidden,
        shown: savedShown,
        hiddenBanners: savedHiddenBanners,
      },
      { order: savedOrder, hidden: arrangement.hidden },
      keepPlaceOf,
    ),
  );
  const [draft, setDraft] = useState<ProductLayout>(initial);
  const [rights, setRights] = useState<RightsChanges>({});
  const [saving, setSaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [announcement, setAnnouncement] = useState('');
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);

  const mounted = useRef(true);
  const region = useRef<HTMLElement>(null);
  const leaveSaveButton = useRef<HTMLButtonElement>(null);
  const leaveOrigin = useRef<HTMLElement | null>(null);
  const gridRef = useRef<HTMLUListElement>(null);
  const drag = useRef<Drag | null>(null);
  const suppressClick = useRef(false);

  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const label = (product: EditorProduct) =>
    t(product.label, { ns: 'products' });

  const current = arrangeProducts(candidates, draft);
  // For the pointer handlers, registered once: what is shown right now.
  const shownProducts = useRef({ candidates, keepPlaceOf });
  shownProducts.current = { candidates, keepPlaceOf };
  const layoutChanged = !sameLayout(draft, initial);
  const hasChanges = layoutChanged || !!Object.keys(rights).length;

  useEffect(() => {
    region.current?.focus();
    return () => {
      mounted.current = false;
    };
  }, []);

  const save = async () => {
    if (saving) return;
    setConfirmLeave(false);
    if (!hasChanges) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ layout: layoutChanged ? draft : null, rights });
      if (mounted.current) onClose();
    } catch (saveError) {
      if (!mounted.current) return;
      setSaving(false);
      setError(saveErrorMessage(saveError));
    }
  };

  // Escape never saves or discards: with changes it asks. An open sheet is a
  // modal and handles its own Escape.
  const requestLeave = () => {
    if (saving) return;
    if (hasChanges) {
      leaveOrigin.current = document.activeElement as HTMLElement | null;
      setConfirmLeave(true);
      return;
    }
    onClose();
  };

  // Back to editing, with focus where the question was asked from.
  const dismissLeave = () => {
    const origin = leaveOrigin.current;
    leaveOrigin.current = null;
    setConfirmLeave(false);
    if (origin?.isConnected && region.current?.contains(origin)) origin.focus();
    else region.current?.focus();
  };

  useEffect(() => {
    if (confirmLeave) leaveSaveButton.current?.focus();
  }, [confirmLeave]);

  const move = (id: string, toIndex: number) => {
    const product = productById.get(id);
    const next = moveShownProduct(draft, candidates, id, toIndex, keepPlaceOf);
    setDraft(next);
    if (product) {
      const grid = arrangeProducts(candidates, next).grid;
      setAnnouncement(
        `${label(product)}: Position ${grid.indexOf(id) + 1} von ${grid.length}`,
      );
    }
  };

  const hide = (id: string) => {
    const product = productById.get(id);
    setDraft((layout) => hideProduct(layout, id));
    if (product) setAnnouncement(`${label(product)} ausgeblendet`);
  };

  const restore = (id: string) => {
    const product = productById.get(id);
    setDraft((layout) =>
      restoreProduct(layout, id, !!product?.hiddenUntilAdded),
    );
    if (product) setAnnouncement(`${label(product)} wird wieder angezeigt`);
  };

  const removeBanner = (banner: DashboardBanner) => {
    setDraft((layout) => hideBanner(layout, banner.id));
    setAnnouncement(`${banner.label} ausgeblendet`);
  };

  const showBanner = (banner: DashboardBanner) => {
    setDraft((layout) => restoreBanner(layout, banner.id));
    setAnnouncement(`${banner.label} wird wieder angezeigt`);
  };

  const startDrag = (event: React.PointerEvent<HTMLButtonElement>, id: string) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    suppressClick.current = false;
    const tile = event.currentTarget.closest('[data-product-id]');
    const rect = (tile ?? event.currentTarget).getBoundingClientRect();
    drag.current = {
      id,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
  };

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const active = drag.current;
      if (!active || event.pointerId !== active.pointerId) return;
      const distance = Math.hypot(event.clientX - active.x, event.clientY - active.y);
      if (!active.moved && distance < DRAG_THRESHOLD_PX) return;
      if (!active.moved) {
        // On the region, which never moves: the reordered tiles would lose
        // the capture. Only for a real drag, so a tap still clicks its tile.
        try {
          region.current?.setPointerCapture?.(active.pointerId);
        } catch {
          // The pointer is already gone; pointerup/pointercancel ends the drag.
        }
      }
      active.moved = true;
      setDragPreview({
        id: active.id,
        left: event.clientX - active.offsetX,
        top: event.clientY - active.offsetY,
        width: active.width,
        height: active.height,
      });
      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest('[data-product-id]')
        ?.getAttribute('data-product-id');
      if (!target || target === active.id) return;
      setDraft((layout) => {
        const { candidates: latest, keepPlaceOf: kept } = shownProducts.current;
        const index = arrangeProducts(latest, layout).grid.indexOf(target);
        return index < 0
          ? layout
          : moveShownProduct(layout, latest, active.id, index, kept);
      });
    };
    const onPointerEnd = (event: PointerEvent) => {
      const active = drag.current;
      if (!active || event.pointerId !== active.pointerId) return;
      // Swallows only the click this drag ends with, wherever it lands.
      suppressClick.current = active.moved;
      window.setTimeout(() => {
        suppressClick.current = false;
      });
      drag.current = null;
      setDragPreview(null);
    };
    // Touch: the touched element's implicit capture ends while the capture
    // moves to the region; only losing it at the region ends the drag.
    const onLostCapture = (event: PointerEvent) => {
      if (event.target === region.current) onPointerEnd(event);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);
    window.addEventListener('lostpointercapture', onLostCapture);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
      window.removeEventListener('lostpointercapture', onLostCapture);
    };
  }, []);

  const onTileKeyDown = (event: React.KeyboardEvent, id: string) => {
    const index = current.grid.indexOf(id);
    const columns = gridColumns(gridRef.current);
    const targets: Record<string, number> = {
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
      ArrowUp: index - columns,
      ArrowDown: index + columns,
      Home: 0,
      End: current.grid.length - 1,
    };
    if (event.key in targets) {
      event.preventDefault();
      move(id, targets[event.key]);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      hide(id);
    }
  };

  const holdersOf = (right: string) => {
    if (right in rights) return rights[right] ?? [];
    return securityPlus?.active ? currentHolders(securityPlus.settings, right) : [];
  };

  const protectionUnavailableReason = (product: EditorProduct) => {
    if (!product.authorize) {
      return `Für ${label(product)} gibt es keinen einstellbaren PIN-Schutz.`;
    }
    if (!securityPlusUsable) {
      return 'Security Plus ist nicht aktiv. Ohne Security Plus gibt es keinen PIN-Schutz.';
    }
    if (product.hide) {
      return `${label(product)} ist nicht aktiv und kann nicht geschützt werden.`;
    }
    return null;
  };

  const openProtection = (product: EditorProduct) => {
    const reason = protectionUnavailableReason(product);
    if (reason) {
      setAnnouncement(reason);
      return;
    }
    setSheet({ kind: 'protection', productId: product.id });
  };

  const applyProtection = (right: string, holders: string[] | null) => {
    const published = securityPlus?.active
      ? currentHolders(securityPlus.settings, right)
      : [];
    const unchanged = sameHolders(holders ?? [], published);
    setRights((staged) => {
      const { [right]: _previous, ...others } = staged;
      return unchanged ? others : { ...others, [right]: holders };
    });
    setSheet(null);
  };

  const leaveTo = (path: string) => {
    onClose();
    history.push(path);
  };

  const addable: AddableProduct[] = current.addable.flatMap((id) => {
    const product = productById.get(id);
    // Not offered while inactive: no longer sold, no website activated by Gastronaut,
    // or its App Store app is off.
    if (
      !product ||
      (product.hide &&
        (product.noLongerSold || product.managedWebsite || product.absentWithoutApp))
    )
      return [];
    return [
      {
        product,
        label: label(product),
        // Inactive products that wait to be added stay status-only: adding
        // never grants access.
        restorable:
          (draft.hidden.includes(id) || current.defaultHidden.includes(id)) &&
          (!product.hide ||
            (!product.dontShowOnHide && !product.hiddenUntilAdded)),
        destination:
          product.hide && product.promotionalLink
            ? product.promotionalLink(restaurantId)
            : null,
      },
    ];
  });

  const protectedProduct =
    sheet?.kind === 'protection' ? productById.get(sheet.productId) : undefined;
  const draggedProduct = dragPreview && productById.get(dragPreview.id);

  const doneButton = (
    <button
      type="button"
      className="product-editor-done"
      disabled={saving}
      onClick={save}
    >
      {saving ? 'Speichert …' : 'Fertig'}
    </button>
  );

  return (
    <section
      ref={region}
      className={classHelper(['product-editor', saving && 'is-saving'])}
      aria-labelledby="product-editor-title"
      aria-busy={saving}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        if (confirmLeave) dismissLeave();
        else requestLeave();
      }}
    >
      {banners.map((banner) =>
        draft.hiddenBanners?.includes(banner.id) ? (
          <div key={banner.id} className="product-editor-message">
            <span>{banner.label} ist ausgeblendet.</span>
            <button
              type="button"
              className="product-editor-button"
              disabled={saving}
              onClick={() => showBanner(banner)}
            >
              Wieder anzeigen
            </button>
          </div>
        ) : (
          <div key={banner.id} className="product-editor-banner">
            <div className="product-editor-banner-content">{banner.content}</div>
            <button
              type="button"
              className="product-editor-remove"
              aria-label={`${banner.label} ausblenden`}
              disabled={saving}
              onClick={() => removeBanner(banner)}
            >
              <Close style={{ fontSize: 14 }} />
            </button>
          </div>
        ),
      )}

      <div className="product-editor-toolbar">
        <button
          type="button"
          className="product-editor-button"
          disabled={saving}
          onClick={() => setSheet({ kind: 'add' })}
        >
          <Add fontSize="small" /> Hinzufügen
        </button>
        <h2 id="product-editor-title">Produkte anordnen</h2>
        {doneButtonContainer
          ? ReactDOM.createPortal(doneButton, doneButtonContainer)
          : doneButton}
      </div>
      <p id="product-editor-hint" className="product-editor-hint">
        Ziehen oder mit den Pfeiltasten verschieben, Entf blendet aus. Mit
        „Fertig“ gilt die Anordnung für alle Nutzer dieses Restaurants.
      </p>

      {!!error && (
        <div className="product-editor-message is-error" role="alert">
          <span>{error} Deine Änderungen bleiben erhalten.</span>
          <button type="button" className="product-editor-button" onClick={save}>
            Erneut speichern
          </button>
        </div>
      )}

      {confirmLeave && (
        <div
          className="product-editor-message"
          role="alertdialog"
          aria-label="Ungespeicherte Änderungen"
        >
          <span>Du hast ungespeicherte Änderungen.</span>
          <button
            ref={leaveSaveButton}
            type="button"
            className="product-editor-button is-primary"
            onClick={save}
          >
            Speichern
          </button>
          <button type="button" className="product-editor-button" onClick={onClose}>
            Verwerfen
          </button>
          <button
            type="button"
            className="product-editor-button"
            onClick={dismissLeave}
          >
            Weiter bearbeiten
          </button>
        </div>
      )}

      <ul ref={gridRef} className="product-icons product-editor-grid">
        {current.grid.map((id) => {
          const product = productById.get(id)!;
          const name = label(product);
          const holders = product.authorize ? holdersOf(product.authorize) : [];
          const unavailableReason = protectionUnavailableReason(product);
          return (
            <li
              key={id}
              data-product-id={id}
              className={classHelper([
                'product-editor-tile',
                dragPreview?.id === id && 'is-placeholder',
              ])}
            >
              <div
                className={classHelper([
                  'inline-column',
                  'product-icon',
                  product.hide && 'hidden',
                ])}
              >
                <button
                  type="button"
                  className="product-editor-handle"
                  aria-label={`${name}, Position ${current.grid.indexOf(id) + 1} von ${current.grid.length}`}
                  aria-describedby="product-editor-hint"
                  disabled={saving}
                  onPointerDown={(event) => startDrag(event, id)}
                  onKeyDown={(event) => onTileKeyDown(event, id)}
                  onClick={() => {
                    if (suppressClick.current) {
                      suppressClick.current = false;
                      return;
                    }
                    if (product.authorize) openProtection(product);
                  }}
                >
                  <Box flex className="center" padding={20} elevation={0}>
                    <img height={40} width={40} alt="" src={`/products/${id}.svg`} />
                  </Box>
                </button>
                <span className="product-editor-label">{name}</span>
              </div>
              <button
                type="button"
                className="product-editor-remove"
                aria-label={`${name} ausblenden`}
                disabled={saving}
                onClick={() => hide(id)}
              >
                <Close style={{ fontSize: 14 }} />
              </button>
              <button
                type="button"
                className={classHelper([
                  'product-editor-shield',
                  unavailableReason
                    ? 'is-unavailable'
                    : holders.length
                    ? 'is-protected'
                    : 'is-open',
                ])}
                aria-label={`Zugriffsschutz ${name}: ${
                  !product.authorize
                    ? 'nicht verfügbar'
                    : holders.length
                    ? 'PIN-Schutz aktiv'
                    : 'kein PIN-Schutz'
                }`}
                aria-disabled={!!unavailableReason}
                title={unavailableReason ?? undefined}
                disabled={saving}
                onClick={() => openProtection(product)}
              >
                <LockedProductIcon width="22" height="22" />
              </button>
            </li>
          );
        })}
      </ul>
      {!current.grid.length && (
        <p className="product-editor-hint">
          Alle Produkte sind ausgeblendet. Über „Hinzufügen“ holst du sie
          zurück.
        </p>
      )}

      <div className="product-editor-sr-only" aria-live="polite">
        {announcement}
      </div>

      {!!dragPreview &&
        !!draggedProduct &&
        ReactDOM.createPortal(
          // A picture of the tile only; the real tile keeps its place and controls.
          <div
            className="product-drag-preview"
            aria-hidden="true"
            style={{
              left: dragPreview.left,
              top: dragPreview.top,
              width: dragPreview.width,
              height: dragPreview.height,
            }}
          >
            <div
              className={classHelper([
                'inline-column',
                'product-icon',
                draggedProduct.hide && 'hidden',
              ])}
            >
              <Box
                flex
                className="center product-editor-art"
                padding={20}
                elevation={0}
              >
                <img
                  height={40}
                  width={40}
                  alt=""
                  src={`/products/${draggedProduct.id}.svg`}
                />
              </Box>
              <span className="product-editor-label">{label(draggedProduct)}</span>
            </div>
          </div>,
          document.body,
        )}

      {sheet?.kind === 'add' && (
        <AddProductsSheet
          items={addable}
          onRestore={restore}
          onOpenDestination={hasChanges ? undefined : leaveTo}
          onClose={() => setSheet(null)}
        />
      )}
      {!!protectedProduct?.authorize && !!securityPlus?.settings && (
        <ProtectionPanel
          productLabel={label(protectedProduct)}
          settings={securityPlus.settings}
          holders={holdersOf(protectedProduct.authorize)}
          onApply={(holders) =>
            applyProtection(protectedProduct.authorize!, holders)
          }
          onCancel={() => setSheet(null)}
          onManageSignatures={
            hasChanges
              ? undefined
              : () =>
                  leaveTo(
                    `/${restaurantId}/settings/general/apps?appId=security_plus`,
                  )
          }
        />
      )}
    </section>
  );
};

export default ProductLayoutEditor;
